/**
 * batch-47 gespreksverslagen (spec 90 §2) - DE resolver.
 *
 * Events zijn de waarheid; hoofdstatus en actiestatus zijn AFGELEID. Dit is de
 * enige plek waar die afleiding gebeurt: de statusbalk, de tijdlijn, de
 * filters, de tellers en de dashboard-widget lezen allemaal hiervan (direct, of
 * via de geindexeerde cache-kolommen die de event-schrijver met exact deze
 * functie bijzet). Geen tweede implementatie, nergens.
 *
 * Pure module: geen `new Date()`, geen db, geen I/O. De caller levert `nu` als
 * ISO-instant, zodat cron, API en tests hetzelfde resultaat krijgen en de
 * DST-conversies op de rand blijven (Europe/Amsterdam).
 */

import type {
  GespreksverslagActieStatus,
  GespreksverslagEventSoort,
  GespreksverslagHoofdstatus,
  GespreksverslagOndertekenaarRol,
  GespreksverslagOndertekenPlicht,
  GespreksverslagStapSleutel,
  GespreksverslagStatusStapDTO,
  GespreksverslagTermijnConfig,
  GespreksverslagTermijnStand,
  GespreksverslagTerControleModus,
  GespreksverslagVerstrekKanaal,
} from '@streep/types'
import { GESPREKSVERSLAG_STAP_LABELS } from '@streep/types'

const DAG_MS = 24 * 60 * 60 * 1000

// ─── Invoer-shapes ───────────────────────────────────────────────────────────

/** Eén tijdlijn-event zoals de resolver hem nodig heeft (geen inhoud). */
export interface ResolverEvent {
  soort: GespreksverslagEventSoort
  /** ISO-instant. */
  moment: string
  versieNr: number | null
  /** Naam van de actor voor de statusbalk-hover (null = systeem/onbekend). */
  actorNaam?: string | null
  reden?: string | null
  kanaal?: GespreksverslagVerstrekKanaal | null
  /** Bij TERMIJN_VERLENGD: het aantal dagen verlenging (uit meta.dagen). */
  extraDagen?: number | null
  /** Bij ondertekenaar-events: op wie het event slaat. */
  ondertekenaarId?: string | null
  /**
   * Is de actor HET ONDERWERP van het verslag? Sinds een mede-ondertekenaar
   * (leidinggevende/HR) het document ook kan openen en bevestigen, mag zijn
   * leesbevestiging het verslag niet op "Gelezen" zetten - dat gaat over de
   * medewerker. Alleen relevant bij GEOPEND/GELEZEN.
   */
  doorOnderwerp?: boolean
}

/** De handtekening-/weiger-stand per verplichte ondertekenaar van DE huidige versie. */
export interface ResolverOndertekenaar {
  id: string
  rol: GespreksverslagOndertekenaarRol
  employeeId: string
  naam: string | null
  /** ISO of null - DocumentSignature op de huidige versie. */
  getekendOp: string | null
  /** ISO of null - ONDERTEKENING_GEWEIGERD-event op deze ondertekenaar. */
  geweigerdOp: string | null
  weigerReden: string | null
  /** ISO of null - §12: HR sloot de openstaande handtekening administratief af. */
  administratiefAfgeslotenOp: string | null
}

export interface GespreksverslagResolverInput {
  /** Alle events van dit verslag, oplopend op moment (de caller sorteert). */
  events: ResolverEvent[]
  /** Laatst vastgelegde versie; 0 = nog nooit vrijgegeven. */
  huidigeVersieNr: number
  /** Verplichte ondertekenaars van de huidige versie (leeg = geen tekenplicht). */
  ondertekenaars: ResolverOndertekenaar[]
  ondertekenPlicht: GespreksverslagOndertekenPlicht
  terControleModus: GespreksverslagTerControleModus
  /** FO-126-leesbevestiging van de medewerker op de huidige versie (ISO of null). */
  leesbevestigingOp: string | null
  /** Stilzwijgend akkoord op de huidige versie (ISO of null). */
  stilzwijgendAkkoordOp: string | null
  /** Aantal open actiepunten (FO-332) - los van de documentstatus (§11). */
  openActiepunten: number
  /** Staat er nog een vervolgafspraak "te plannen" open (§11a)? */
  vervolgTePlannen: boolean
  /** Termijnconfiguratie van het gesprekstype (§7). */
  termijnConfig: GespreksverslagTermijnConfig
  /** ISO-instant "nu". */
  nu: string
}

export interface GespreksverslagStatusResultaat {
  hoofdstatus: GespreksverslagHoofdstatus
  actieStatus: GespreksverslagActieStatus
  stappen: GespreksverslagStatusStapDTO[]
  termijn: GespreksverslagTermijnStand
  /** Aantallen voor "2 van 3 getekend"; vereist 0 = geen tekenplicht. */
  ondertekenStand: { vereist: number; getekend: number; geweigerd: number }
  /** Is het akkoord-traject afgerond (getekend/gelezen/stilzwijgend/gearchiveerd)? */
  trajectAfgerond: boolean
}

// ─── Hulpfuncties ────────────────────────────────────────────────────────────

function ms(iso: string | null | undefined): number | null {
  if (!iso) return null
  const t = Date.parse(iso)
  return Number.isNaN(t) ? null : t
}

/** Het laatste event van een soort (events zijn oplopend gesorteerd). */
function laatste(events: ResolverEvent[], soort: GespreksverslagEventSoort): ResolverEvent | null {
  for (let i = events.length - 1; i >= 0; i -= 1) {
    if (events[i].soort === soort) return events[i]
  }
  return null
}

/** Het eerste event van een soort. */
function eerste(events: ResolverEvent[], soort: GespreksverslagEventSoort): ResolverEvent | null {
  for (const e of events) if (e.soort === soort) return e
  return null
}

/**
 * Events die bij de HUIDIGE versie horen: alles ná het VERSIE_GEMAAKT/VERZONDEN
 * van die versie. Events zonder versieNr (concept-fase, gebackfilde historie)
 * tellen mee zolang de versie 1 is. Zo telt een v1-handtekening niet mee voor
 * het v2-traject (FO-378-regel, doorgetrokken naar de tijdlijn).
 */
export function eventsVanVersie(events: ResolverEvent[], versieNr: number): ResolverEvent[] {
  if (versieNr <= 1) return events.filter((e) => e.versieNr == null || e.versieNr <= 1)
  return events.filter((e) => e.versieNr === versieNr)
}

// ─── Termijnstand (§7) ───────────────────────────────────────────────────────

/**
 * Bereken de termijnstand uit de events. De klok start bij het VERZONDEN-moment
 * van de huidige versie en telt pauzes (TERMIJN_GEPAUZEERD -> TERMIJN_HERVAT)
 * er weer af, zodat een langdurig zieke geen "verlopen"-status oploopt (T11).
 * TERMIJN_VERLENGD schuift de uiterste datum met `extraDagen` op.
 */
export function berekenTermijnStand(params: {
  events: ResolverEvent[]
  config: GespreksverslagTermijnConfig
  /** Is het traject al afgerond? Dan loopt er geen klok meer. */
  trajectAfgerond: boolean
  nu: string
}): GespreksverslagTermijnStand {
  const { events, config, trajectAfgerond } = params
  const nuMs = ms(params.nu) ?? 0
  const verzonden = laatste(events, 'VERZONDEN')
  const startMs = ms(verzonden?.moment ?? null)

  let pauzeMs = 0
  let pauzeStart: number | null = null
  let pauzeReden: string | null = null
  let verlengdMetDagen = 0
  for (const e of events) {
    const t = ms(e.moment)
    if (t == null || startMs == null || t < startMs) {
      // Pauzes van vóór de huidige verzending tellen niet mee.
      if (e.soort === 'TERMIJN_VERLENGD') continue
      continue
    }
    if (e.soort === 'TERMIJN_GEPAUZEERD' && pauzeStart == null) {
      pauzeStart = t
      pauzeReden = e.reden ?? null
    } else if (e.soort === 'TERMIJN_HERVAT' && pauzeStart != null) {
      pauzeMs += Math.max(0, t - pauzeStart)
      pauzeStart = null
      pauzeReden = null
    } else if (e.soort === 'TERMIJN_VERLENGD') {
      verlengdMetDagen += Math.max(0, Math.round(e.extraDagen ?? 0))
    }
  }
  const gepauzeerd = pauzeStart != null
  if (gepauzeerd && pauzeStart != null) pauzeMs += Math.max(0, nuMs - pauzeStart)

  if (startMs == null) {
    return {
      gestart: false,
      gepauzeerd: false,
      pauzeReden: null,
      dagenOpen: 0,
      verlooptOp: null,
      verlopen: false,
      pauzeDagen: 0,
      verlengdMetDagen: 0,
    }
  }

  const effectiefMs = Math.max(0, nuMs - startMs - pauzeMs)
  const verlooptMs =
    startMs + (config.verlopenDagen + verlengdMetDagen) * DAG_MS + pauzeMs
  return {
    gestart: !trajectAfgerond,
    gepauzeerd,
    pauzeReden,
    dagenOpen: Math.floor(effectiefMs / DAG_MS),
    verlooptOp: new Date(verlooptMs).toISOString(),
    // Gepauzeerd = nooit verlopen (T11); afgerond ook niet.
    verlopen: !trajectAfgerond && !gepauzeerd && nuMs >= verlooptMs,
    pauzeDagen: Math.floor(pauzeMs / DAG_MS),
    verlengdMetDagen,
  }
}

// ─── De resolver ─────────────────────────────────────────────────────────────

/** Volgorde van de ladder; hoger = verder in het proces. */
const LADDER: GespreksverslagHoofdstatus[] = [
  'CONCEPT',
  'TER_CONTROLE',
  'DEFINITIEF',
  'VERZONDEN',
  'GEOPEND',
  'GELEZEN',
  'WACHT_OP_ONDERTEKENING',
  'GEDEELTELIJK_ONDERTEKEND',
  'VOLLEDIG_ONDERTEKEND',
]

/**
 * Leidt hoofdstatus + actiestatus + statusbalk af uit de events en de
 * handtekening-/leesbevestigingsstand. Zie spec 90 §2/§3/§4.
 */
export function bepaalGespreksverslagStatus(
  input: GespreksverslagResolverInput,
): GespreksverslagStatusResultaat {
  const alle = [...input.events].sort((a, b) => a.moment.localeCompare(b.moment))
  const versieNr = Math.max(1, input.huidigeVersieNr)
  const huidig = eventsVanVersie(alle, versieNr)

  // ── Feiten uit de events ──
  const aangemaakt = eerste(alle, 'AANGEMAAKT') ?? alle[0] ?? null
  const definitief = laatste(huidig, 'DEFINITIEF')
  const ondertekendStreep = laatste(huidig, 'ONDERTEKEND_STREEP')
  const verzonden = laatste(huidig, 'VERZONDEN')
  const geopend = huidig.find((e) => e.soort === 'GEOPEND' && e.doorOnderwerp !== false) ?? null
  const gelezenEvent =
    huidig.find((e) => e.soort === 'GELEZEN' && e.doorOnderwerp !== false) ?? null
  const stilzwijgend = laatste(huidig, 'STILZWIJGEND_AKKOORD')
  const gearchiveerd = laatste(alle, 'GEARCHIVEERD')
  const heropendNa = laatste(alle, 'HEROPEND')
  // Een NIEUWE VERSIE heft de archivering op (T6): volledige ondertekening
  // archiveert automatisch, en zonder deze regel zou het verslag daarna voor
  // altijd "Gearchiveerd" blijven - ook als HR een v2 start, verstuurt en
  // opnieuw laat tekenen. Dan zou de statusbalk liegen, het dashboard het
  // verslag niet meer als werkvoorraad tonen en de termijn-cron het overslaan.
  const versieNa = laatste(alle, 'VERSIE_GEMAAKT')

  // Ter controle: aangeboden en nog geen besluit erna.
  const controleAangeboden = laatste(huidig, 'TER_CONTROLE_AANGEBODEN')
  const controleGoedgekeurd = laatste(huidig, 'TER_CONTROLE_GOEDGEKEURD')
  const controleTerug = laatste(huidig, 'TER_CONTROLE_TERUGGESTUURD')
  const controleOpen =
    controleAangeboden != null &&
    (controleGoedgekeurd == null || controleGoedgekeurd.moment < controleAangeboden.moment) &&
    (controleTerug == null || controleTerug.moment < controleAangeboden.moment)

  // Archivering geldt tenzij er daarna heropend is OF een nieuwe versie is
  // gestart (die heropent het traject per definitie: nieuw akkoord gevraagd).
  const isGearchiveerd =
    gearchiveerd != null &&
    (heropendNa == null || heropendNa.moment < gearchiveerd.moment) &&
    (versieNa == null || versieNa.moment < gearchiveerd.moment)

  // ── Ondertekenstand ──
  const vereist = input.ondertekenaars.length
  const getekend = input.ondertekenaars.filter((o) => o.getekendOp != null).length
  const geweigerd = input.ondertekenaars.filter((o) => o.geweigerdOp != null).length
  const afgehandeld = input.ondertekenaars.filter(
    (o) => o.getekendOp != null || o.administratiefAfgeslotenOp != null,
  ).length
  const heeftTekenplicht = input.ondertekenPlicht !== 'GEEN' && vereist > 0
  const volledigOndertekend = heeftTekenplicht && afgehandeld >= vereist && geweigerd === 0

  const leesbevestigd = input.leesbevestigingOp != null || gelezenEvent != null
  const stilzwijgendAkkoord =
    input.stilzwijgendAkkoordOp != null || stilzwijgend != null

  // Traject afgerond: getekend, geweigerd (een weigering IS een uitkomst - HR
  // bepaalt de opvolging, de klok hoeft niet door te tikken), of (zonder
  // tekenplicht) gelezen/stilzwijgend.
  const trajectAfgerond =
    isGearchiveerd ||
    volledigOndertekend ||
    geweigerd > 0 ||
    (!heeftTekenplicht && (leesbevestigd || stilzwijgendAkkoord))

  const termijn = berekenTermijnStand({
    events: huidig,
    config: input.termijnConfig,
    trajectAfgerond,
    nu: input.nu,
  })

  // ── Ladder: hoogste bereikte rung ──
  const bereikt = new Set<GespreksverslagHoofdstatus>(['CONCEPT'])
  if (controleOpen) bereikt.add('TER_CONTROLE')
  if (definitief) bereikt.add('DEFINITIEF')
  if (verzonden) bereikt.add('VERZONDEN')
  if (geopend) bereikt.add('GEOPEND')
  if (leesbevestigd || stilzwijgendAkkoord) bereikt.add('GELEZEN')
  if (verzonden && heeftTekenplicht) {
    bereikt.add('WACHT_OP_ONDERTEKENING')
    if (getekend > 0 && !volledigOndertekend) bereikt.add('GEDEELTELIJK_ONDERTEKEND')
    if (volledigOndertekend) bereikt.add('VOLLEDIG_ONDERTEKEND')
  }
  let hoofdstatus: GespreksverslagHoofdstatus = 'CONCEPT'
  for (const stap of LADDER) if (bereikt.has(stap)) hoofdstatus = stap

  // ── Uitzonderingstoestanden (winnen van de ladder) ──
  if (geweigerd > 0 && !isGearchiveerd) hoofdstatus = 'ONDERTEKENING_GEWEIGERD'
  else if (termijn.verlopen) hoofdstatus = 'VERLOPEN'
  if (isGearchiveerd) hoofdstatus = 'GEARCHIVEERD'

  // ── Actiestatus ──
  const actieStatus = bepaalActieStatus({
    hoofdstatus,
    heeftTekenplicht,
    ondertekenaars: input.ondertekenaars,
    verzonden: verzonden != null,
    leesbevestigd: leesbevestigd || stilzwijgendAkkoord,
    termijnVerlopen: termijn.verlopen,
    openActiepunten: input.openActiepunten,
    vervolgTePlannen: input.vervolgTePlannen,
  })

  return {
    hoofdstatus,
    actieStatus,
    stappen: bouwStatusbalk({
      terControleModus: input.terControleModus,
      heeftTekenplicht,
      hoofdstatus,
      aangemaakt,
      controleAfgerond: controleGoedgekeurd,
      controleOpen,
      definitief,
      ondertekendStreep,
      verzonden,
      gelezen: gelezenEvent ?? stilzwijgend,
      gelezenOp: input.leesbevestigingOp ?? input.stilzwijgendAkkoordOp ?? null,
      volledigOndertekend,
      ondertekenaars: input.ondertekenaars,
      ondertekenPlichtVanType: input.ondertekenPlicht,
      gearchiveerd: isGearchiveerd ? gearchiveerd : null,
    }),
    termijn,
    ondertekenStand: { vereist, getekend, geweigerd },
    trajectAfgerond,
  }
}

function bepaalActieStatus(p: {
  hoofdstatus: GespreksverslagHoofdstatus
  heeftTekenplicht: boolean
  ondertekenaars: ResolverOndertekenaar[]
  verzonden: boolean
  leesbevestigd: boolean
  termijnVerlopen: boolean
  openActiepunten: number
  vervolgTePlannen: boolean
}): GespreksverslagActieStatus {
  if (p.termijnVerlopen) return 'TERMIJN_OVERSCHREDEN'
  if (p.hoofdstatus === 'ONDERTEKENING_GEWEIGERD') return 'ACTIE_HR'
  if (p.hoofdstatus === 'GEARCHIVEERD') {
    if (p.openActiepunten > 0) return 'OPEN_ACTIEPUNTEN'
    return 'GEEN'
  }
  // Vóór verzending ligt de bal bij de opsteller/controleur.
  if (!p.verzonden) return 'ACTIE_LEIDINGGEVENDE'

  if (p.heeftTekenplicht) {
    const open = p.ondertekenaars.filter(
      (o) => o.getekendOp == null && o.geweigerdOp == null && o.administratiefAfgeslotenOp == null,
    )
    if (open.length > 0) {
      // De medewerker eerst; daarna leidinggevende, daarna HR.
      if (open.some((o) => o.rol === 'MEDEWERKER')) return 'ACTIE_MEDEWERKER'
      if (open.some((o) => o.rol === 'LEIDINGGEVENDE')) return 'ACTIE_LEIDINGGEVENDE'
      return 'ACTIE_HR'
    }
  } else if (!p.leesbevestigd) {
    return 'ACTIE_MEDEWERKER'
  }

  if (p.openActiepunten > 0) return 'OPEN_ACTIEPUNTEN'
  if (p.vervolgTePlannen) return 'VERVOLG_TE_PLANNEN'
  return 'GEEN'
}

// ─── Statusbalk (§4) ─────────────────────────────────────────────────────────

function stap(
  sleutel: GespreksverslagStapSleutel,
  staat: GespreksverslagStapStaatIntern,
  op: string | null,
  actorNaam: string | null,
  toelichting: string | null,
): GespreksverslagStatusStapDTO {
  return { sleutel, label: GESPREKSVERSLAG_STAP_LABELS[sleutel], staat, op, actorNaam, toelichting }
}

type GespreksverslagStapStaatIntern = GespreksverslagStatusStapDTO['staat']

function bouwStatusbalk(p: {
  terControleModus: GespreksverslagTerControleModus
  heeftTekenplicht: boolean
  hoofdstatus: GespreksverslagHoofdstatus
  aangemaakt: ResolverEvent | null
  controleAfgerond: ResolverEvent | null
  controleOpen: boolean
  definitief: ResolverEvent | null
  ondertekendStreep: ResolverEvent | null
  verzonden: ResolverEvent | null
  gelezen: ResolverEvent | null
  gelezenOp: string | null
  volledigOndertekend: boolean
  ondertekenaars: ResolverOndertekenaar[]
  /** Ondertekenplicht van het TYPE (los van de nog niet vastgelegde snapshot). */
  ondertekenPlichtVanType: GespreksverslagOndertekenPlicht
  gearchiveerd: ResolverEvent | null
}): GespreksverslagStatusStapDTO[] {
  const stappen: GespreksverslagStatusStapDTO[] = []

  stappen.push(
    stap('CONCEPT', 'VOLTOOID', p.aangemaakt?.moment ?? null, p.aangemaakt?.actorNaam ?? null, null),
  )

  if (p.terControleModus === 'UIT') {
    // A1: bij "UIT" hoort de stap niet in de balk (bestaande werkwijze).
    if (p.controleAfgerond || p.controleOpen) {
      stappen.push(
        stap(
          'TER_CONTROLE',
          p.controleAfgerond ? 'VOLTOOID' : 'HUIDIG',
          p.controleAfgerond?.moment ?? null,
          p.controleAfgerond?.actorNaam ?? null,
          p.controleAfgerond?.reden ?? null,
        ),
      )
    }
  } else {
    stappen.push(
      stap(
        'TER_CONTROLE',
        p.controleAfgerond ? 'VOLTOOID' : p.controleOpen ? 'HUIDIG' : 'TOEKOMSTIG',
        p.controleAfgerond?.moment ?? null,
        p.controleAfgerond?.actorNaam ?? null,
        p.controleAfgerond?.reden ?? null,
      ),
    )
  }

  stappen.push(
    stap(
      'DEFINITIEF',
      p.definitief ? 'VOLTOOID' : 'TOEKOMSTIG',
      p.definitief?.moment ?? null,
      p.definitief?.actorNaam ?? null,
      p.ondertekendStreep
        ? `Namens STREEP ondertekend door ${p.ondertekendStreep.actorNaam ?? 'STREEP'}`
        : null,
    ),
  )
  stappen.push(
    stap(
      'VERZONDEN',
      p.verzonden ? 'VOLTOOID' : 'TOEKOMSTIG',
      p.verzonden?.moment ?? null,
      p.verzonden?.actorNaam ?? null,
      p.verzonden?.reden ?? null,
    ),
  )
  const gelezenOp = p.gelezenOp ?? p.gelezen?.moment ?? null
  stappen.push(
    stap(
      'GELEZEN',
      gelezenOp ? 'VOLTOOID' : 'TOEKOMSTIG',
      gelezenOp,
      p.gelezen?.actorNaam ?? null,
      null,
    ),
  )

  if (p.heeftTekenplicht) {
    const getekend = p.ondertekenaars.filter((o) => o.getekendOp != null)
    const laatsteHandtekening = getekend
      .map((o) => o.getekendOp)
      .filter((x): x is string => x != null)
      .sort()
      .at(-1)
    const geweigerd = p.ondertekenaars.find((o) => o.geweigerdOp != null)
    stappen.push(
      stap(
        'ONDERTEKEND',
        geweigerd
          ? 'HUIDIG'
          : p.volledigOndertekend
            ? 'VOLTOOID'
            : getekend.length > 0
              ? 'HUIDIG'
              : 'TOEKOMSTIG',
        p.volledigOndertekend ? laatsteHandtekening ?? null : null,
        null,
        geweigerd
          ? `Geweigerd door ${geweigerd.naam ?? 'ondertekenaar'}: ${geweigerd.weigerReden ?? 'geen reden'}`
          : `${getekend.length} van ${p.ondertekenaars.length} getekend`,
      ),
    )
  } else if (p.ondertekenPlichtVanType && p.ondertekenPlichtVanType !== 'GEEN') {
    // Het TYPE vraagt wel om handtekeningen, maar de ondertekenaars worden pas
    // bij het verstrekken vastgelegd. "Geen ondertekening vereist" zou hier
    // liegen tegen de opsteller.
    stappen.push(
      stap(
        'ONDERTEKEND',
        'TOEKOMSTIG',
        null,
        null,
        'Ondertekenaars worden vastgelegd bij het verstrekken',
      ),
    )
  } else {
    // A3: zonder tekenplicht is stilzwijgend akkoord de afrondingsroute.
    stappen.push(
      stap('ONDERTEKEND', 'OVERGESLAGEN', null, null, 'Geen ondertekening vereist voor dit type'),
    )
  }

  stappen.push(
    stap(
      'GEARCHIVEERD',
      p.gearchiveerd ? 'VOLTOOID' : 'TOEKOMSTIG',
      p.gearchiveerd?.moment ?? null,
      p.gearchiveerd?.actorNaam ?? null,
      p.gearchiveerd?.reden ?? null,
    ),
  )

  // De eerste niet-voltooide, niet-overgeslagen stap is de huidige (tenzij al
  // expliciet HUIDIG gezet, bv. gedeeltelijk ondertekend of geweigerd).
  if (!stappen.some((s) => s.staat === 'HUIDIG')) {
    const idx = stappen.findIndex((s) => s.staat === 'TOEKOMSTIG')
    if (idx >= 0) stappen[idx] = { ...stappen[idx], staat: 'HUIDIG' }
  }
  return stappen
}

// ─── Tijdlijn-omschrijvingen (§4) ────────────────────────────────────────────

/**
 * Kant-en-klare NL-zin per event, gedeeld door backoffice en portal zodat beide
 * exact dezelfde tijdlijn tonen. Bevat NOOIT gespreksinhoud: alleen wie/wat/
 * wanneer, en de proces-reden die de actor zelf invulde.
 */
export function beschrijfGespreksverslagEvent(e: {
  soort: GespreksverslagEventSoort
  actorNaam?: string | null
  systeem?: boolean
  versieNr?: number | null
  kanaal?: GespreksverslagVerstrekKanaal | null
  reden?: string | null
  meta?: Record<string, string | number | boolean | null> | null
}): string {
  const wie = e.systeem ? 'Het systeem' : e.actorNaam ?? 'Iemand'
  const versie = e.versieNr && e.versieNr > 1 ? ` (versie ${e.versieNr})` : ''
  const rol = typeof e.meta?.rol === 'string' ? String(e.meta.rol).toLowerCase() : null
  const naam = typeof e.meta?.naam === 'string' ? String(e.meta.naam) : null

  switch (e.soort) {
    case 'AANGEMAAKT':
      return `${wie} heeft het verslag aangemaakt`
    case 'GEWIJZIGD':
      return `${wie} heeft het concept bijgewerkt`
    case 'TER_CONTROLE_AANGEBODEN':
      return `${wie} heeft het verslag ter controle aangeboden${naam ? ` aan ${naam}` : ''}`
    case 'TER_CONTROLE_GOEDGEKEURD':
      return `${wie} heeft het verslag goedgekeurd`
    case 'TER_CONTROLE_TERUGGESTUURD':
      return `${wie} heeft het verslag teruggestuurd naar de opsteller`
    case 'DEFINITIEF':
      return `${wie} heeft het verslag definitief gemaakt${versie}`
    case 'ONDERTEKEND_STREEP':
      return `${wie} heeft namens STREEP ondertekend`
    case 'VERZONDEN':
      return `${wie} heeft het verslag verstrekt aan de medewerker${versie}`
    case 'GEOPEND':
      return `${wie} heeft het verslag geopend`
    case 'GELEZEN':
      return `${wie} heeft bevestigd het verslag te hebben gelezen (geen akkoord)`
    case 'GEDOWNLOAD':
      return `${wie} heeft het verslag gedownload${versie}`
    case 'GETEKEND':
      return `${wie} heeft ondertekend${rol ? ` als ${rol}` : ''}${versie}`
    case 'ONDERTEKENING_GEWEIGERD':
      return `${wie} heeft ondertekening geweigerd`
    case 'ONDERTEKENING_ADMINISTRATIEF_AFGESLOTEN':
      return `${wie} heeft de openstaande ondertekening administratief afgesloten${naam ? ` voor ${naam}` : ''}`
    case 'STILZWIJGEND_AKKOORD':
      return `Stilzwijgend akkoord geregistreerd (geen reactie binnen de termijn, geen handtekening)`
    case 'REACTIE':
      return `${wie} heeft gereageerd${typeof e.meta?.soort === 'string' ? ` (${String(e.meta.soort).toLowerCase()})` : ''}`
    case 'HERINNERD':
      return `Herinnering verstuurd${naam ? ` aan ${naam}` : ''}`
    case 'VERLOPEN':
      return `De termijn is verstreken`
    case 'TERMIJN_GEPAUZEERD':
      return `${wie} heeft de termijn gepauzeerd`
    case 'TERMIJN_HERVAT':
      return `${wie} heeft de termijn hervat`
    case 'TERMIJN_VERLENGD':
      return `${wie} heeft de termijn verlengd${e.meta?.dagen ? ` met ${e.meta.dagen} dagen` : ''}`
    case 'VERSIE_GEMAAKT':
      return `${wie} heeft een nieuwe versie gestart${versie}`
    case 'HEROPEND':
      return `${wie} heeft het verslag heropend`
    case 'ADDENDUM_TOEGEVOEGD':
      return `${wie} heeft een correctieblad toegevoegd${naam ? `: ${naam}` : ''}`
    case 'DEELNEMER_GEWIJZIGD':
      return `${wie} heeft de deelnemers gewijzigd`
    case 'BIJLAGE_GEWIJZIGD':
      return `${wie} heeft de bijlagen gewijzigd`
    case 'ONDERTEKENAARS_VASTGESTELD':
      return `De verplichte ondertekenaars zijn vastgesteld${e.meta?.aantal ? ` (${e.meta.aantal})` : ''}`
    case 'VERVOLGAFSPRAAK_INGEPLAND':
      return `${wie} heeft de vervolgafspraak ingepland${naam ? ` (${naam})` : ''}`
    case 'ACTIEPUNT_AFGEROND':
      return `${wie} heeft een actiepunt afgerond`
    case 'ACTIEPUNT_HEROPEND':
      return `${wie} heeft een actiepunt heropend`
    case 'GEARCHIVEERD':
      return `${wie} heeft het verslag gearchiveerd`
    default:
      return `${wie} heeft een wijziging doorgevoerd`
  }
}

/**
 * Welk kanaal is er als laatste gebruikt om te verstrekken? Voedt de
 * "alternatieve verstrekking"-weergave (§12).
 */
export function laatsteVerstrekKanaal(
  events: ResolverEvent[],
): GespreksverslagVerstrekKanaal | null {
  const verzonden = laatste(
    [...events].sort((a, b) => a.moment.localeCompare(b.moment)),
    'VERZONDEN',
  )
  return verzonden?.kanaal ?? null
}
